/**
 * Domain model for the agent/master console.
 *
 * Nomenclature (owner directive 2026-07-29, specs/features/agent-console.yaml):
 * the UI says AGENT and MEMBER. `node` is hierarchy-service's internal word and
 * must not appear in any user-facing string. In this file `Account` is the
 * structural union — an Account is an AGENT when `role !== 'player'` and a
 * MEMBER when `role === 'player'`. Nothing renders the word "account" either;
 * it is a type name, not copy.
 *
 * Money invariant: every monetary field on these types is a LEDGER PROJECTION.
 * Nothing here is an optimistic local value, and the client layer never mutates
 * a balance in place after a write — it refetches.
 */

// Type-only, erased at build. The data layer picks the icon and the badge tone
// for a search hit because the palette renders four different kinds through one
// row component; making the renderer re-derive them would put a switch on kind
// in a component whose whole job is not to care.
import type { IconName, Tone } from '@w7/ui'

/* ------------------------------------------------------------- hierarchy -- */

/**
 * The ladder, root first. Matches the Winner7/PG365 industry ladder so operators
 * moving across products read the same words, while `isAgent` keeps the spec's
 * agent/member split intact.
 */
export const ROLES = [
  'company-master',
  'triple-master',
  'double-master',
  'super-master',
  'master',
  'player',
] as const

export type Role = (typeof ROLES)[number]

/**
 * User-facing labels.
 *
 * The leaf renders as MEMBER, never "Player" — acceptance criterion in
 * specs/features/agent-console.yaml:93, owner directive 2026-07-29. The agent
 * tiers keep the Winner7/PG365 ladder names so operators moving between
 * products read the same words.
 *
 * ⚠ KNOWN SPEC DIVERGENCE, tracked in docs/spec-delta.md: the genesis feature
 * spec names its agent tiers Super Master (L1 tenant root) → Sub-Agent (L5),
 * where Winner7 puts Super Master at depth 3. The two vocabularies collide on
 * that one label. This console uses the Winner7 ladder because that is the
 * product being replaced; the spec amendment PR carries the reconciliation.
 */
export const ROLE_LABEL: Record<Role, string> = {
  'company-master': 'Company Master',
  'triple-master': 'Triple Master',
  'double-master': 'Double Master',
  'super-master': 'Super Master',
  master: 'Master',
  player: 'Member',
}

/** Short form for dense table cells and chips where the full label will not fit. */
export const ROLE_SHORT: Record<Role, string> = {
  'company-master': 'CM',
  'triple-master': 'TM',
  'double-master': 'DM',
  'super-master': 'SM',
  master: 'M',
  player: 'MBR',
}

export const isAgent = (role: Role): boolean => role !== 'player'
export const roleDepth = (role: Role): number => ROLES.indexOf(role)

/**
 * Roles that can sit beneath this actor anywhere in their subtree
 * (exclusive of self). Used for downline role filters and max-credit ladders.
 *
 * Company Master → TM, DM, SM, Master, Member
 * Triple Master → DM, SM, Master, Member
 * Double Master → SM, Master, Member
 * …
 */
export function descendantRoles(actor: Role): Role[] {
  if (actor === 'player') return []
  return ROLES.slice(roleDepth(actor) + 1)
}

/** The roles a given role is allowed to create directly beneath itself. */
export function creatableRoles(actor: Role): Role[] {
  return descendantRoles(actor)
}

export type AccountStatus = 'active' | 'inactive' | 'suspended' | 'closed'

/* -------------------------------------------------------------- stopping -- */

/**
 * The suspension in force on an account, projected for display.
 *
 * `accountId` is the account the suspension was actually PLACED ON, which is
 * this account when it was stopped directly and an ANCESTOR of it when the stop
 * reaches it down the chain. `byId` is who placed it. The two are different
 * questions — "what has to be lifted" and "who may lift it" — and a stopped
 * member needs both answered on the row.
 *
 * Stopping is deliberately a different authority model from credit: credit moves
 * only between an account and its DIRECT parent, while a stop may be placed by
 * any strict ancestor at any depth. A Company Master who sees something wrong
 * five levels down stops it now, not through four intermediaries.
 */
export interface StoppedBy {
  /** The account carrying the suspension — this account, or one above it. */
  accountId: string
  username: string
  /** The account that placed it. */
  byId: string
  byUsername: string
  at: string
  /** Required when the stop was placed; the next person needs to know what was seen. */
  reason: string
}

/**
 * The ONE status control this row should offer the signed-in caller, decided in
 * the data layer.
 *
 * The lift rule (a suspension may be lifted only by the account that placed it
 * or by someone above that account) needs subtree knowledge the client does not
 * have, so a screen cannot re-derive it — it would have to guess, and guessing
 * wrong means a master under suspicion quietly un-suspends their own downline.
 * `setAccountStatus` enforces the same decision server-side; this is that
 * decision precomputed so the button can be rendered honestly.
 */
export interface StopControl {
  action: 'stop' | 'lift' | 'none'
  /** Present when `action` is 'none' — the sentence to show, already written. */
  refusal: string | null
}

/** The stop projection carried by everything that renders an account. */
export type StopState = Pick<Account, 'effectiveStatus' | 'stoppedBy' | 'stopControl'>

/**
 * The stop projection for a record whose source has no suspension store — the
 * live hierarchy service today.
 *
 * Honest rather than optimistic: the status is the record's own and no control
 * is offered, because nothing behind that record could enforce one.
 */
export function unstopped(status: AccountStatus, refusal: string | null = null): StopState {
  return { effectiveStatus: status, stoppedBy: null, stopControl: { action: 'none', refusal } }
}

/** Spreadable "nothing is stopping this account" for fixtures and test factories. */
export const NOT_STOPPED: StopState = Object.freeze(unstopped('active'))

/**
 * A flag is a computed attention signal, never stored state. Each one is
 * something an operator can act on today; we deliberately do not surface
 * fraud/collusion cases here — that is an explicit non-goal of the console
 * (specs/features/agent-console.yaml, non_goals[0]).
 */
export type AccountFlag =
  | 'exposure-near-limit' // exposure > 80% of credit limit
  | 'exposure-over-limit' // exposure exceeds credit limit — bets will start rejecting
  | 'dormant-funded' // no activity 14d+ but still holds a balance
  | 'settlement-overdue' // owed either way for more than 7 days
  | 'never-logged-in' // created but never signed in
  | 'no-login-yet' // hierarchy record exists, account provisioning does not (#149)

export interface Account {
  id: string
  username: string
  displayName: string
  role: Role
  parentId: string | null
  /** Ancestor ids, root first, excluding self. Drives the breadcrumb and scoping. */
  path: string[]
  /**
   * This account's OWN stored status — never what a row should render.
   *
   * An account beneath a stopped agent is stopped while its own status stays
   * exactly what it was, which is the whole point: lifting the one suspension
   * restores the branch with every descendant's own status intact. Render
   * `effectiveStatus`; `status` is what survives a lift.
   */
  status: AccountStatus

  /**
   * Status once the whole upline chain is applied. DERIVED per read, never
   * written onto descendants.
   *
   * Required, not optional, and that is the point: an account cannot reach a
   * screen without its stopped-ness having been worked out, so no screen can
   * forget to ask.
   */
  effectiveStatus: AccountStatus
  /** null when nothing on or above this account is stopping it. */
  stoppedBy: StoppedBy | null
  /** What the SIGNED-IN caller may do to this account's status right now. */
  stopControl: StopControl

  createdAt: string
  lastActiveAt: string | null

  /** Lab currencyType (INR / POINTS / POINT@1). Inherited from TM. */
  currencyType?: 'INR' | 'POINTS' | 'POINT@1'
  /** Lab casinoCurrencyType — Evolution HKD|AED (CM→TM). */
  casinoCurrencyType?: 'HKD' | 'AED'
  /** Lab fundistCurrencyType — Crash Games. */
  fundistCurrencyType?: 'HKD' | 'AED' | 'USD' | 'SGD' | 'INR'
  /** Ceiling this account may hold, set by its parent. */
  creditLimit: number
  /**
   * Max credit this agent may assign to each level beneath them (Winner7 cascade).
   * Set at create; missing means no per-level cap beyond remaining credit.
   */
  downlineMaxCredit?: Partial<Record<Role, number>>
  /** Live ledger balance (TigerBeetle). */
  balance: number
  /**
   * Credit distributed to direct children (Σ child creditLimits).
   * Agents only — 0 for members.
   */
  distributedBalance: number
/**
   * Current worst-case liability for this node. For agents this is their
   * retained partnership share of player/downline bets (not full stake).
   * Soft 0 when the exposure projector is down/stale (never a leftover fake non-zero).
   */
  exposure: number
  /** True when Redis exposure was soft-zeroed due to stale/unavailable projector. */
  exposureStale?: boolean
  /**
   * Net settlement position with the PARENT, from the parent's point of view.
   * Positive = the parent is owed this much (a "take"); negative = the parent
   * owes it (a "give"). One signed number replaces the original product's two
   * mutually-exclusive Take and Give columns.
   */
  settlement: number

  /** Percentage points this account keeps of its downline's loss. */
  share: { sportsbook: number; casino: number }
  /**
   * Lab Member.rate[] for Info popover (SportsBook / Live Casino).
   * From hierarchy tree when present; else synthesized from share bp.
   */
  rate?: import('@/api/map/lab-rate').LabRateRow[]
  /** Commission percentage points paid down to this account's downline. */
  commission: { fancy: number; bookmaker: number; matchOdds: number }
  betLimits: { min: number; max: number }
  games: GameSwitches
  /** Sportswise / product On-Off from hierarchy `game_flags` (missing = on). */
  gameFlags?: import('@/api/map/gameLock').GameFlags
  /** Lab padlocks from hierarchy `game_locks`. */
  gameLocks?: import('@/api/map/gameLock').GameLocks

  /** Direct children count — lets the tree render a disclosure without loading. */
  childCount: number
  /** Every player at or beneath this account. 0 for a player. */
  memberCount: number

  flags: AccountFlag[]
  /** Free-text note captured at creation. */
  comments?: string
}

/**
 * The STORED account row: every field except the three the data layer derives
 * on the way out.
 *
 * The fixture world and the live mappers build one of these, and only the data
 * client turns it into an `Account`. That is a compiler-enforced boundary, not a
 * convention — a read path that forgot to work out whether an account is stopped
 * would not typecheck, and a convention would be forgotten on the fifth screen.
 */
export type AccountRecord = Omit<Account, 'effectiveStatus' | 'stoppedBy' | 'stopControl'>

export interface GameSwitches {
  casino: boolean
  cardGame: boolean
  liveGame: boolean
  sportsbook: boolean
}

export const GAME_LABEL: Record<keyof GameSwitches, string> = {
  casino: 'Casino',
  cardGame: 'Card Game',
  liveGame: 'Live Game',
  sportsbook: 'Sportsbook',
}

/* ----------------------------------------------------------------- money -- */

/**
 * A credit movement between an account and its direct parent.
 * `direction` is expressed from the ACTING agent's point of view — the console
 * always renders it relative to whoever is signed in, never absolutely.
 */
export interface Transfer {
  id: string
  /** Human reference shown in the statement and quoted in support. */
  ref: string
  at: string
  fromId: string
  toId: string
  amount: number
  direction: 'give' | 'take'
  remark: string
  /** Ledger balances after the movement, both sides — proves reconciliation. */
  balanceAfterFrom: number
  balanceAfterTo: number
  /** Who authorised it. */
  byId: string
}

/* ------------------------------------------------------------- exchange --- */

export type Sport = 'Cricket' | 'Soccer' | 'Tennis' | 'Horse Racing' | 'Greyhound Racing' | 'Casino'

export const SPORTS: Sport[] = ['Cricket', 'Soccer', 'Tennis', 'Horse Racing', 'Greyhound Racing', 'Casino']

export type MarketType =
  | 'Match Odds'
  | 'Bookmaker'
  | 'Toss Market'
  | 'Fancy'
  | 'Live Casino'

export type BetSide = 'back' | 'lay'

export type BetStatus = 'unmatched' | 'matched' | 'settled' | 'void' | 'cancelled'

export interface Bet {
  id: string
  memberId: string
  placedAt: string
  /**
   * When the FIRST of this stake matched. Null while nothing has matched.
   *
   * The original back-office showed Place time and Matched time side by side
   * because the gap between them is the operator's only read on whether the
   * exchange is absorbing their members' money or leaving it hanging. For a
   * part-matched bet this is the first fill, not the last — a bet that is still
   * filling has no "matched at" yet to report.
   */
  matchedAt: string | null
  sport: Sport
  eventId: string
  eventName: string
  marketId: string
  marketName: string
  marketType: MarketType
  selection: string
  side: BetSide
  oddsPlaced: number
  oddsMatched: number | null
  stake: number
  matched: number
  unmatched: number
  /**
   * What the MEMBER stands to lose if this bet loses. For a back bet that is the
   * matched stake; for a lay bet it is matched * (odds - 1).
   */
  liability: number
  /** What the MEMBER stands to win if this bet wins, net of stake. */
  potentialProfit: number
  status: BetStatus
  result?: 'won' | 'lost' | 'void'
  /** Member P&L once settled. Negative = member lost = the book won. */
  pnl?: number
  // NOTE: deliberately no surveillance/fraud field here. "NOT a surveillance or
  // fraud tool ... not even the knowledge that a case exists on your own
  // downline" is an architectural boundary, not a permission
  // (specs/features/agent-console.yaml non_goals[0]). An earlier draft of this
  // file carried a `flagged` flag; it was removed rather than hidden.
}

/**
 * Worst/best case for one market, per selection.
 * `net[i]` is the book's position if selection i wins — positive is a win for
 * the book, negative is a payout.
 */
export interface MarketExposure {
  marketId: string
  marketName: string
  marketType: MarketType
  selections: { name: string; net: number }[]
  worstCase: number
  bestCase: number
  matched: number
  unmatched: number
  betCount: number
  memberCount: number
}

export interface EventExposure {
  eventId: string
  eventName: string
  sport: Sport
  startsAt: string
  inPlay: boolean
  markets: MarketExposure[]
  worstCase: number
  bestCase: number
  betCount: number
  memberCount: number
}

/* ------------------------------------------------------- price ladder ----- */

export interface PriceLevel {
  /** Decimal odds, already snapped to an exchange tick. */
  price: number
  /** Money available at that price. */
  size: number
}

/**
 * One runner's side of the exchange ladder.
 *
 * `back` is ordered OUTERMOST → BEST (depth 2, 1, 0) and `lay` BEST → OUTERMOST
 * (depth 0, 1, 2), so rendered left to right the two best prices sit adjacent in
 * the middle and the outer prices fade away from the spread — the W7 rule "best
 * price innermost, outer prices faded". The ordering lives in the type because
 * it is the only place it survives contact with a second renderer.
 */
export interface RunnerPrices {
  selection: string
  back: PriceLevel[]
  lay: PriceLevel[]
  /**
   * ALWAYS true in v1. No exchange price feed is connected to this console and
   * these prices are derived from the fixture's own probabilities.
   *
   * This is a FLAG, not a comment, and that is the point: a renderer cannot
   * destructure this shape without the compiler putting the word "simulated" in
   * front of it. A figure that could be mistaken for a real Betfair price is
   * dangerous in a way that a figure labelled in prose is not.
   */
  simulated: true
}

/* ------------------------------------------------------------ book rows --- */

/** One selection's position on somebody's book. Positive = the book keeps it. */
export interface BookPosition {
  name: string
  net: number
}

/**
 * A per-selection book row for one account in the market's hierarchy tree.
 *
 * The root, every branch and the hedge projection all render through one row
 * component, so they are literally the same shape. Three near-identical shapes
 * could silently misalign their columns, which on this table means reading
 * Pakistan's number under Sri Lanka's header.
 */
export interface BookBranchRow {
  account: Account
  /** In the market's selection order, always — never re-sorted per row. */
  positions: BookPosition[]
  worstCase: number
  bestCase: number
  betCount: number
  /** Members at or beneath this account holding a position on THIS market. */
  memberCount: number
  /** True when at least one child also holds a position here — gates the expander. */
  hasChildren: boolean
}

/**
 * The simulated exchange reconciliation pair.
 *
 * In production a non-zero difference between the two rows means the exchange
 * and the platform's own record of the hedge disagree and must be reconciled
 * before settlement. Here both rows are generated from the fixture, which is why
 * `simulated` is on the envelope rather than left to the caller to remember.
 */
export interface SimulatedExchangeBook {
  simulated: true
  exchange: BookPosition[]
  local: BookPosition[]
}

/* -------------------------------------------------------------- reporting -- */

/** The game bucket the original product reported on — kept because operators use it. */
export type GameBucket =
  | 'SportsBook'
  | 'SportsBook - Bookmaker'
  | 'Fancy'
  | 'Live Casino'
  | 'Card Game'
  | 'Credit Limit'

/**
 * Lab PL Statement “Select Type” → `Account/getDownlineAccountsData` `typeName`.
 * Same static list as project Backend-UI `PLStatementCtrl.getTypes`.
 */
export type PlTypeName =
  | 'Match Odds'
  | 'Completed Match'
  | 'Tied Match'
  | 'Bookmaker'
  | 'Session'

/**
 * One settled P&L row at member grain.
 *
 * `memberPnl` is the MEMBER's result: negative means the member lost, which is a
 * win for the book. Every upline figure derives from it by share, so the console
 * never has to trust two independently-computed numbers to agree.
 */
export interface PnlRow {
  id: string
  settledAt: string
  memberId: string
  sport: Sport
  game: GameBucket
  /** Stable market identifier when the reporting source carries it. */
  marketId?: string
  /**
   * The event INSTANCE, not the fixture template.
   *
   * `eventName` repeats — the same race card and the same league fixture come
   * round again every few days — so anything grouping by event has to group on
   * this. Grouping on the name silently adds a month of different matches
   * together and labels the total with one match's name.
   */
  eventId: string
  eventName: string
  marketName: string
  memberPnl: number
  commission: number
  /** memberPnl net of commission — what actually moved. */
  netAmount: number
  /** Per-role slice of `-netAmount`, keyed by role. Sums to `-netAmount`. */
  split: Partial<Record<Role, number>>
  betCount: number
  turnover: number
  /** Member settlement currency (INR / POINTS) — drives ₹ vs Pts display. */
  currencyType?: string
}

/** A day of the book, at whatever scope the query was made. */
export interface DailyPoint {
  date: string // isoDay
  turnover: number
  grossPnl: number
  commission: number
  netPnl: number
  bets: number
  activeMembers: number
  newMembers: number
  creditIn: number
  creditOut: number
}

/* ------------------------------------------------------------- activity --- */

export type ActivityKind =
  | 'transfer'
  | 'account-created'
  | 'limit-changed'
  | 'status-changed'
  | 'login'
  | 'password-reset'
  | 'settlement'

export interface ActivityEvent {
  id: string
  at: string
  kind: ActivityKind
  actorId: string
  subjectId: string
  summary: string
  detail?: string
  amount?: number
  ip?: string
  device?: string
}

/* ---------------------------------------------------------------- search -- */

/**
 * The four things the console can be searched for.
 *
 * `action` is declared here but is NEVER returned by the data layer: actions and
 * pages are matched client-side so they appear on the first keystroke regardless
 * of network latency. It lives in the union so one row component and one score
 * ladder cover every hit the palette can show.
 *
 * There is deliberately no separate `selection` kind. A runner is only findable
 * because a market carries an open position on it, so a selection hit belongs in
 * the same group as the market it sits on; giving it its own group would split
 * the live book into two lists that answer the same question.
 */
export type SearchKind = 'account' | 'market' | 'transfer' | 'action'

export interface SearchHit {
  id: string
  kind: SearchKind
  /** The string that is rendered, and the string `ranges` index into. */
  title: string
  subtitle: string
  /** Dim trailing detail — counts and timing only. Never money: see `badge`. */
  meta?: string
  /** [start,end) offsets into `title` for the highlight marks. */
  ranges: [number, number][]
  /** Always an in-app path. */
  to: string
  icon: IconName
  score: number
  /**
   * The one figure a hit is allowed to carry. Money badges come from the
   * exposure projection, so they are omitted entirely when it is stale — a
   * palette row has no room for the unavailability banner, and a stale number is
   * never rendered.
   */
  badge?: { label: string; tone: Tone }
  /**
   * The tier, on account hits only.
   *
   * Carried separately from `badge` because `RoleChip` picks its colour from the
   * role, not from a label — and the tier ladder is the one thing an operator
   * scans a list of usernames for. Without it the palette could only render a
   * flat muted chip reading "SM", which is the tier's name but not its rank.
   */
  role?: Role
  /**
   * The stop projection, on account hits only.
   *
   * A palette that sends an operator to a stopped account without saying so is
   * the fifth screen that forgot. It is a separate field rather than a whole
   * `Account` because a hit renders three strings and a chip, not a row.
   */
  stop?: StopState
}

export interface SearchGroup {
  kind: SearchKind
  label: string
  hits: SearchHit[]
  /** Matches BEFORE the per-kind cap, so the palette can say "showing 5 of 41". */
  total: number
}

export interface SearchResult {
  /**
   * Echoed back so a late response for a two-character query is discarded rather
   * than painted over the results for a six-character one.
   */
  text: string
  groups: SearchGroup[]
  /**
   * True when the exposure projection is stale. The live-book group is then
   * empty and the palette prints a one-line note instead of a figure — the
   * StaleProjection rule applied to a surface with no room for the full banner.
   */
  exposureUnavailable: boolean
}

/* ----------------------------------------------------------------- misc --- */

/**
 * Wraps any figure that comes from the exposure projection.
 *
 * A stale projection is NEVER rendered as a number (feature acceptance:
 * "A stale exposure projection renders the unavailability banner, never a
 * number"), so the projection's freshness travels with its value rather than
 * being tracked separately and forgotten.
 */
export interface Projected<T> {
  value: T
  /** When the projection last advanced. */
  asOf: string
  stale: boolean
}

export interface Paged<T> {
  rows: T[]
  total: number
  page: number
  pageSize: number
}

export interface DateRange {
  from: string // isoDay
  to: string // isoDay
}

/** Who is signed in, and therefore what the console is allowed to render. */
export interface Session {
  accountId: string
  username: string
  displayName: string
  role: Role
  /** Extra claims beyond the hierarchy role. */
  scopes: ('ops' | 'risk-desk')[]
}
